/**
 * useFieldGroups — React hook for managing repeatable field groups.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  fieldGroupsAPI,
  FieldGroup,
  FieldGroupCreate,
  FieldGroupUpdate,
} from '@/services/api/fieldGroups';

interface UseFieldGroupsReturn {
  groups: FieldGroup[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  createGroup: (data: FieldGroupCreate) => Promise<FieldGroup | null>;
  updateGroup: (id: number, data: FieldGroupUpdate) => Promise<FieldGroup | null>;
  setChildren: (id: number, fieldIds: number[]) => Promise<FieldGroup | null>;
  deleteGroup: (id: number) => Promise<boolean>;
}

function extractError(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((e: any) => e.msg || String(e)).join('; ');
  return detail || err?.message || fallback;
}

export default function useFieldGroups(): UseFieldGroupsReturn {
  const [groups, setGroups] = useState<FieldGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fieldGroupsAPI.list();
      setGroups(data);
    } catch (err: any) {
      setError(extractError(err, 'Failed to load field groups'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const createGroup = useCallback(async (data: FieldGroupCreate) => {
    try {
      const created = await fieldGroupsAPI.create(data);
      setGroups((prev) => [...prev, created].sort((a, b) => a.friendly_name.localeCompare(b.friendly_name)));
      return created;
    } catch (err: any) {
      setError(extractError(err, 'Failed to create field group'));
      return null;
    }
  }, []);

  const updateGroup = useCallback(async (id: number, data: FieldGroupUpdate) => {
    try {
      const updated = await fieldGroupsAPI.update(id, data);
      setGroups((prev) => prev.map((g) => (g.id === id ? updated : g)));
      return updated;
    } catch (err: any) {
      setError(extractError(err, 'Failed to update field group'));
      return null;
    }
  }, []);

  const setChildren = useCallback(async (id: number, fieldIds: number[]) => {
    try {
      const updated = await fieldGroupsAPI.setChildren(id, fieldIds);
      setGroups((prev) => prev.map((g) => (g.id === id ? updated : g)));
      return updated;
    } catch (err: any) {
      setError(extractError(err, 'Failed to update group children'));
      return null;
    }
  }, []);

  const deleteGroup = useCallback(async (id: number) => {
    try {
      await fieldGroupsAPI.delete(id);
      setGroups((prev) => prev.filter((g) => g.id !== id));
      return true;
    } catch (err: any) {
      setError(extractError(err, 'Failed to delete field group'));
      return false;
    }
  }, []);

  return { groups, loading, error, refetch, createGroup, updateGroup, setChildren, deleteGroup };
}
